// ISDS A2-4.3.12: Patch Management & Security Updates Admin UI
import React, { useState, useEffect } from "react";
import {
    Box,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Card,
    CardContent,
    Grid,
    Divider,
    IconButton,
    Tooltip,
    LinearProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SecurityIcon from "@mui/icons-material/Security";
import UpdateIcon from "@mui/icons-material/Update";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BugReportIcon from "@mui/icons-material/BugReport";
import HistoryIcon from "@mui/icons-material/History";
import { API_BASE } from "../../apiBase";

export default function PatchManagementTab({ headers }) {
    const [scanning, setScanning] = useState(false);
    const [scanResult, setScanResult] = useState(null);
    const [error, setError] = useState("");
    const [lastScanDate, setLastScanDate] = useState(null);
    const [updateHistory, setUpdateHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Load scan history on mount
    useEffect(() => {
        loadUpdateHistory();
    }, []);

    const loadUpdateHistory = async () => {
        setLoadingHistory(true);
        try {
            const res = await fetch(`${API_BASE}/admin/patch-management/history`, {
                headers,
            });
            if (res.ok) {
                const data = await res.json();
                setUpdateHistory(data.history || []);
                if (data.last_scan) {
                    setLastScanDate(new Date(data.last_scan));
                }
            }
        } catch (err) {
            console.error("Failed to load update history:", err);
        } finally {
            setLoadingHistory(false);
        }
    };

    const runSecurityScan = async () => {
        setScanning(true);
        setError("");
        setScanResult(null);

        try {
            const res = await fetch(`${API_BASE}/admin/patch-management/scan`, {
                method: "POST",
                headers,
            });

            if (res.ok) {
                const data = await res.json();
                setScanResult(data);
                setLastScanDate(new Date());
                loadUpdateHistory(); // Refresh history
            } else {
                const errorData = await res.json();
                setError(errorData.error || "Security scan failed");
            }
        } catch (err) {
            setError("Failed to run security scan: " + err.message);
        } finally {
            setScanning(false);
        }
    };

    const getSeverityColor = (severity) => {
        switch (severity?.toLowerCase()) {
            case "critical":
                return "error";
            case "high":
                return "error";
            case "moderate":
            case "medium":
                return "warning";
            case "low":
                return "info";
            default:
                return "default";
        }
    };

    const getSeverityIcon = (count) => {
        if (count === 0) return <CheckCircleIcon color="success" />;
        return <WarningIcon color="error" />;
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <SecurityIcon sx={{ mr: 2, fontSize: 32 }} />
                <Typography variant="h4">Patch Management & Security Updates</Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                ISDS A2-4.3.12: Monitor and manage security updates for all system dependencies.
            </Typography>

            {/* Action Buttons */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={runSecurityScan}
                        disabled={scanning}
                        startIcon={scanning ? <CircularProgress size={20} /> : <BugReportIcon />}
                    >
                        {scanning ? "Scanning..." : "Run Security Scan"}
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={loadUpdateHistory}
                        disabled={loadingHistory}
                        startIcon={<HistoryIcon />}
                    >
                        Refresh History
                    </Button>

                    {lastScanDate && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
                            Last scan: {lastScanDate.toLocaleString()}
                        </Typography>
                    )}
                </Box>
            </Paper>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
                    {error}
                </Alert>
            )}

            {/* Scanning Progress */}
            {scanning && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Running Security Scan...
                    </Typography>
                    <LinearProgress sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                        Checking Python dependencies, JavaScript packages, and Docker images for known vulnerabilities...
                    </Typography>
                </Paper>
            )}

            {/* Scan Results */}
            {scanResult && (
                <>
                    <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
                        Security Scan Results
                    </Typography>

                    {/* Summary Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={4}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                        {getSeverityIcon(scanResult.summary?.critical || 0)}
                                        <Typography variant="h6" sx={{ ml: 1 }}>
                                            Python Dependencies
                                        </Typography>
                                    </Box>
                                    <Typography variant="h3" color={scanResult.python?.vulnerabilities_count > 0 ? "error" : "success.main"}>
                                        {scanResult.python?.vulnerabilities_count || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        vulnerabilities found
                                    </Typography>
                                    {scanResult.python?.outdated_count > 0 && (
                                        <Chip
                                            label={`${scanResult.python.outdated_count} outdated`}
                                            size="small"
                                            color="warning"
                                            sx={{ mt: 1 }}
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                        {getSeverityIcon(scanResult.summary?.high || 0)}
                                        <Typography variant="h6" sx={{ ml: 1 }}>
                                            JavaScript Dependencies
                                        </Typography>
                                    </Box>
                                    <Typography variant="h3" color={scanResult.javascript?.total_vulnerabilities > 0 ? "error" : "success.main"}>
                                        {scanResult.javascript?.total_vulnerabilities || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        vulnerabilities found
                                    </Typography>
                                    <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                        {scanResult.javascript?.critical > 0 && (
                                            <Chip label={`${scanResult.javascript.critical} critical`} size="small" color="error" />
                                        )}
                                        {scanResult.javascript?.high > 0 && (
                                            <Chip label={`${scanResult.javascript.high} high`} size="small" color="error" />
                                        )}
                                        {scanResult.javascript?.moderate > 0 && (
                                            <Chip label={`${scanResult.javascript.moderate} moderate`} size="small" color="warning" />
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                        <UpdateIcon color="primary" />
                                        <Typography variant="h6" sx={{ ml: 1 }}>
                                            Docker Images
                                        </Typography>
                                    </Box>
                                    <Typography variant="body1">
                                        {scanResult.docker?.images_checked || 0} images checked
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {scanResult.docker?.up_to_date ? "All up to date" : "Updates available"}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Detailed Vulnerabilities */}
                    {scanResult.vulnerabilities && scanResult.vulnerabilities.length > 0 && (
                        <>
                            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                                Vulnerabilities Details
                            </Typography>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Package</TableCell>
                                            <TableCell>Severity</TableCell>
                                            <TableCell>Vulnerability ID</TableCell>
                                            <TableCell>Current Version</TableCell>
                                            <TableCell>Fixed In</TableCell>
                                            <TableCell>Description</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {scanResult.vulnerabilities.map((vuln, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{vuln.package}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={vuln.severity}
                                                        size="small"
                                                        color={getSeverityColor(vuln.severity)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <code>{vuln.id}</code>
                                                </TableCell>
                                                <TableCell>{vuln.current_version}</TableCell>
                                                <TableCell>{vuln.fixed_version || "N/A"}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                                        {vuln.description}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}

                    {/* Recommendations */}
                    {scanResult.recommendations && scanResult.recommendations.length > 0 && (
                        <>
                            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                                Recommendations
                            </Typography>
                            <Paper sx={{ p: 2 }}>
                                <ul>
                                    {scanResult.recommendations.map((rec, idx) => (
                                        <li key={idx}>
                                            <Typography variant="body2">{rec}</Typography>
                                        </li>
                                    ))}
                                </ul>
                            </Paper>
                        </>
                    )}
                </>
            )}

            {/* Update History */}
            {updateHistory.length > 0 && (
                <>
                    <Divider sx={{ my: 4 }} />
                    <Typography variant="h5" gutterBottom>
                        Update History
                    </Typography>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Action</TableCell>
                                    <TableCell>Component</TableCell>
                                    <TableCell>Details</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {updateHistory.map((entry, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>
                                            {new Date(entry.timestamp).toLocaleString()}
                                        </TableCell>
                                        <TableCell>{entry.action}</TableCell>
                                        <TableCell>{entry.component}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 400 }}>
                                                {entry.details}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={entry.status}
                                                size="small"
                                                color={entry.status === "completed" ? "success" : "default"}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            {/* Help Section */}
            <Paper sx={{ p: 2, mt: 4, bgcolor: "info.light" }}>
                <Typography variant="subtitle2" gutterBottom>
                    <strong>Documentation:</strong>
                </Typography>
                <Typography variant="body2">
                    • Full guide: <code>ISDS/A2-4.3.12_Patch_Management.md</code>
                    <br />
                    • Quick start: <code>ISDS/A2-4.3.12_QUICK_START.md</code>
                    <br />
                    • CLI commands: <code>scripts/check-updates.sh</code>
                </Typography>
            </Paper>
        </Box>
    );
}
